import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { useAuth, isGlobalAdmin } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import StatsSummaryCards from '../components/StatsSummaryCards';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Team {
  teamId: string;
  name: string;
  description?: string;
  members: { userId: string; role: string; accessLevel: string }[];
}

interface ProjectMember {
  userId: string;
  role: string;
  accessLevel: 'viewer' | 'editor' | 'admin';
  name?: string;
  email?: string;
  isGlobalAdmin?: boolean;
  globalAdminLabel?: string;
}

type ProjectType = 'pi-based' | 'regular-sprints' | 'weekly' | 'monthly';

interface Project {
  projectId: string;
  shortName: string;
  description: string;
  projectType?: ProjectType;
  members: ProjectMember[];
  userAccessLevel: 'viewer' | 'editor' | 'admin';
}

interface ProjectYearConfig {
  id?: string;
  projectId?: string;
  year: number;
  startDate: string | null;
  isConfigured: boolean;
}

interface Sprint {
  sprintId: string;
  piId?: string;
  piNumber?: number;
  sprintNumber: number;
  sprintInPi?: number;
  name: string;
  startDate: string;
  endDate: string;
}

interface ProductIncrement {
  piId: string;
  year: number;
  piNumber: number;
  name: string;
  isSpecial: boolean;
  sprints: Sprint[];
}

interface PIWithSprints extends ProductIncrement {
  sprints: Sprint[];
}

interface ScheduleData {
  projectType: ProjectType;
  // For PI-based: items are PIWithSprints[]; for others: items are Sprint[]
  items: (PIWithSprints | Sprint)[];
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  'pi-based': 'PI-based (SAFe)',
  'regular-sprints': 'Regular Sprints',
  'weekly': 'Weekly',
  'monthly': 'Monthly',
};

const PROJECT_ROLES = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'scrum_master', label: 'Scrum Master' },
  { value: 'member', label: 'Member' },
];

const ACCESS_LEVELS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

interface User {
  userId: string;
  email: string;
  name: string;
  role?: string;
}

interface ProjectSprintDataPoint {
  sprintId: string;
  sprintName: string;
  piName: string;
  costs: number;
  revenue: number;
  costOverRevenue: number;
  accumulatedCosts: number;
  accumulatedRevenue: number;
  accumulatedCostOverRevenue: number;
}

interface ProjectStatistics {
  projectId: string;
  year: number;
  totalCosts: number;
  totalRevenue: number;
  costOverRevenue: number;
  sprintData: ProjectSprintDataPoint[];
}

const currentYear = new Date().getFullYear();

// Chart options factory for theme support
const getChartOptions = (isDark: boolean) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: { color: isDark ? '#9ca3af' : '#4b5563' },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
      ticks: { color: isDark ? '#9ca3af' : '#4b5563' },
    },
    x: {
      grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
      ticks: { color: isDark ? '#9ca3af' : '#4b5563' },
    },
  },
});

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const { theme } = useTheme();
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [editProject, setEditProject] = useState({ shortName: '', description: '' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<'viewer' | 'editor' | 'admin'>('admin');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [showInitSchedule, setShowInitSchedule] = useState(false);
  const [initStartDate, setInitStartDate] = useState('');
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/api/projects/${projectId}`),
  });

  const { data: teamsData, isLoading: teamsLoading } = useQuery({
    queryKey: ['project-teams', projectId],
    queryFn: () => api.get<{ items: Team[] }>(`/api/projects/${projectId}/teams`),
  });

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => api.get<{ items: ProjectMember[] }>(`/api/projects/${projectId}/members`),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ items: User[] }>('/api/users'),
    enabled: showAddMember,
  });

  const { data: statistics, isLoading: statsLoading } = useQuery({
    queryKey: ['project-statistics', projectId, selectedYear],
    queryFn: () => api.get<ProjectStatistics>(`/api/projects/${projectId}/statistics?year=${selectedYear}`),
  });

  const { data: yearsData } = useQuery({
    queryKey: ['project-years', projectId],
    queryFn: () => api.get<{ items: ProjectYearConfig[] }>(`/api/projects/${projectId}/years`),
  });

  const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['project-schedule', projectId, selectedYear],
    queryFn: () => api.get<ScheduleData>(`/api/projects/${projectId}/years/${selectedYear}/schedule`),
    enabled: !!(yearsData?.items?.some((y) => y.year === selectedYear && y.isConfigured)),
  });

  const createTeamMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      api.post(`/api/projects/${projectId}/teams`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-teams', projectId] });
      setShowCreateTeam(false);
      setNewTeam({ name: '', description: '' });
      toast.success('Team created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create team');
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: (data: { shortName: string; description: string }) =>
      api.put(`/api/projects/${projectId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowEditProject(false);
      toast.success('Project updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update project');
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => api.delete(`/api/projects/${projectId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted successfully');
      navigate('/projects');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete project');
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { userId: string; accessLevel: string }) =>
      api.post(`/api/projects/${projectId}/members`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setShowAddMember(false);
      setSelectedUserId('');
      toast.success('Member added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add member');
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { role?: string; accessLevel?: string } }) =>
      api.put(`/api/projects/${projectId}/members/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setEditingMember(null);
      toast.success('Member updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update member');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success('Member removed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove member');
    },
  });

  const initScheduleMutation = useMutation({
    mutationFn: (data: { startDate?: string }) =>
      api.post(`/api/projects/${projectId}/years/${selectedYear}/initialize`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-years', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-schedule', projectId, selectedYear] });
      setShowInitSchedule(false);
      setInitStartDate('');
      toast.success(`Year ${selectedYear} initialized successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to initialize year');
    },
  });

  const deleteYearMutation = useMutation({
    mutationFn: (year: number) =>
      api.delete(`/api/projects/${projectId}/years/${year}`),
    onSuccess: (_, year) => {
      queryClient.invalidateQueries({ queryKey: ['project-years', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-schedule', projectId, year] });
      toast.success(`Year ${year} schedule deleted`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete year');
    },
  });

  const updateSprintDatesMutation = useMutation({
    mutationFn: ({ sprintId, startDate, endDate, cascade }: { sprintId: string; startDate: string; endDate: string; cascade: boolean }) =>
      api.put(`/api/sprints/${sprintId}/dates`, { startDate, endDate, cascade }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-schedule', projectId, selectedYear] });
      setEditingSprint(null);
      toast.success('Sprint dates updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update sprint dates');
    },
  });

  const handleDeleteYear = async (year: number) => {
    const confirmed = await confirm({
      title: 'Delete Year Schedule',
      message: `Delete all PIs and sprints for ${year}? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteYearMutation.mutate(year);
    }
  };

  const handleEditProject = () => {
    if (project) {
      setEditProject({ shortName: project.shortName, description: project.description || '' });
      setShowEditProject(true);
    }
  };

  const handleDeleteProject = async () => {
    const confirmed = await confirm({
      title: 'Delete Project',
      message: `Are you sure you want to delete "${project?.shortName}"? This will also delete all teams in this project. This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      deleteProjectMutation.mutate();
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    const confirmed = await confirm({
      title: 'Remove Member',
      message: `Are you sure you want to remove "${member.name || member.email}" from this project?`,
      confirmText: 'Remove',
      variant: 'danger',
    });

    if (confirmed) {
      removeMemberMutation.mutate(member.userId);
    }
  };

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Project not found</p>
        <Link to="/projects" className="btn-primary mt-4 inline-block">
          Back to Projects
        </Link>
      </div>
    );
  }

  const teams = teamsData?.items || [];
  const members = membersData?.items || [];
  const canAdmin = project.userAccessLevel === 'admin';
  const canDelete = isGlobalAdmin(currentUser);

  // Filter out users who are already members or are global admins
  const availableUsers = usersData?.items.filter(
    (user) => !members.some((m) => m.userId === user.userId) &&
      user.role !== 'superadmin' && user.role !== 'admin'
  ) || [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 dark:text-gray-400">
        <Link to="/projects" className="hover:text-gray-900 dark:hover:text-white">Projects</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 dark:text-white">{project.shortName}</span>
      </nav>

      {/* Project header */}
      <div className="card">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{project.shortName}</h1>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 dark:bg-dark-500 text-gray-600 dark:text-gray-300">
                {PROJECT_TYPE_LABELS[project.projectType || 'pi-based']}
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{project.description || 'No description'}</p>
          </div>
          {canAdmin && (
            <div className="flex gap-2">
              <button onClick={handleEditProject} className="btn-outline">Edit</button>
              {canDelete && (
                <button onClick={handleDeleteProject} className="btn-outline text-primary-400 hover:bg-primary-500/20 border-primary-500/50">
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-dark-500 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Members:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">{members.length}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Teams:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">{teams.length}</span>
          </div>
        </div>
      </div>

      {/* Project Statistics section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Year Statistics</h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="input py-1.5 px-3 w-24"
          >
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {statsLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : !statistics || statistics.sprintData.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No metrics data available for {selectedYear}</p>
            <p className="text-sm mt-1">Add sprint metrics to teams to see project statistics</p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="mb-6">
              <StatsSummaryCards totalCosts={statistics.totalCosts} totalRevenue={statistics.totalRevenue} costOverRevenue={statistics.costOverRevenue} />
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Costs & Revenue Chart */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">Costs & Revenue per Sprint</h3>
                <div className="h-64">
                  <Line
                    data={{
                      labels: statistics.sprintData.map((s) => s.sprintName),
                      datasets: [
                        {
                          label: 'Costs',
                          data: statistics.sprintData.map((s) => s.costs),
                          borderColor: '#E31837', // Crimson Red
                          backgroundColor: 'rgba(227, 24, 55, 0.1)',
                          tension: 0.3,
                        },
                        {
                          label: 'Revenue',
                          data: statistics.sprintData.map((s) => s.revenue),
                          borderColor: '#2DE2BF', // Aquamarine
                          backgroundColor: 'rgba(45, 226, 191, 0.1)',
                          tension: 0.3,
                        },
                      ],
                    }}
                    options={getChartOptions(theme === 'dark')}
                  />
                </div>
              </div>

              {/* C/R Ratio Chart */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">C/R Ratio (Sprint & Accumulated)</h3>
                <div className="h-64">
                  <Line
                    data={{
                      labels: statistics.sprintData.map((s) => s.sprintName),
                      datasets: [
                        {
                          label: 'Sprint C/R',
                          data: statistics.sprintData.map((s) => s.costOverRevenue),
                          borderColor: '#0066FF', // Deep Vivid Blue
                          backgroundColor: 'rgba(0, 102, 255, 0.1)',
                          tension: 0.3,
                        },
                        {
                          label: 'Accumulated C/R',
                          data: statistics.sprintData.map((s) => s.accumulatedCostOverRevenue),
                          borderColor: '#D0006F', // Cyclamen Purple
                          backgroundColor: 'rgba(208, 0, 111, 0.1)',
                          tension: 0.3,
                          borderDash: [5, 5],
                        },
                      ],
                    }}
                    options={getChartOptions(theme === 'dark')}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Project Members section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Project Members</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Admins can manage the project and all teams. Editors can edit data. Viewers can only view.
            </p>
          </div>
          {canAdmin && (
            <button onClick={() => setShowAddMember(true)} className="btn-primary">
              Add Member
            </button>
          )}
        </div>

        {membersLoading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : members.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">No members assigned to this project</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-500">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Access</th>
                  {canAdmin && (
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.userId} className="border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{member.name || 'Unknown'}</span>
                        {member.userId === currentUser?.userId && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            You
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{member.email || '-'}</td>
                    <td className="py-3 px-4">
                      {member.isGlobalAdmin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                          {member.globalAdminLabel}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {PROJECT_ROLES.find((r) => r.value === member.role)?.label || member.role}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.isGlobalAdmin || member.accessLevel === 'admin'
                            ? 'bg-primary-500/20 text-primary-600 dark:text-primary-400'
                            : member.accessLevel === 'editor'
                              ? 'bg-accent-blue/20 text-blue-600 dark:text-blue-400'
                              : 'bg-gray-100 dark:bg-dark-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {member.isGlobalAdmin ? 'Full Access' : ACCESS_LEVELS.find((a) => a.value === member.accessLevel)?.label || member.accessLevel}
                      </span>
                    </td>
                    {canAdmin && (
                      <td className="text-right py-3 px-4">
                        {!member.isGlobalAdmin && (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => setEditingMember(member)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-accent-blue hover:bg-accent-blue/10 transition-colors"
                              title="Edit member"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveMember(member)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                              title="Remove member"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Teams section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Teams</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Teams belong to this project and track their own sprint metrics.
            </p>
          </div>
          {canAdmin && (
            <button onClick={() => setShowCreateTeam(true)} className="btn-primary">
              Add Team
            </button>
          )}
        </div>

        {teamsLoading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : teams.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">No teams yet</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {teams.map((team) => (
              <Link
                key={team.teamId}
                to={`/teams/${team.teamId}`}
                className="p-4 bg-gray-50 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-500 hover:border-primary-500/50 hover:bg-gray-100 dark:hover:bg-dark-500 transition-colors"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">{team.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 line-clamp-2">{team.description || 'No description'}</p>
                <p className="text-gray-500 text-sm mt-2">{team.members?.length || 0} members</p>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Schedule section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Schedule</h2>
          <div className="flex items-center gap-3">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="input py-1.5 px-3 w-24"
            >
              {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {canAdmin && !yearsData?.items?.some((y) => y.year === selectedYear && y.isConfigured) && (
              <button onClick={() => setShowInitSchedule(true)} className="btn-primary">
                Initialize Year
              </button>
            )}
            {canAdmin && yearsData?.items?.some((y) => y.year === selectedYear && y.isConfigured) && (
              <button
                onClick={() => handleDeleteYear(selectedYear)}
                className="btn-outline text-primary-400 hover:bg-primary-500/20 border-primary-500/50"
              >
                Delete Year
              </button>
            )}
          </div>
        </div>

        {!yearsData?.items?.some((y) => y.year === selectedYear && y.isConfigured) ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No schedule configured for {selectedYear}</p>
            {canAdmin && (
              <p className="text-sm mt-1">Click "Initialize Year" to create the schedule</p>
            )}
          </div>
        ) : scheduleLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : (scheduleData?.projectType || project.projectType || 'pi-based') === 'pi-based' ? (
          /* PI-based: show PIs with nested sprints */
          <div className="space-y-4">
            {((scheduleData?.items || []) as PIWithSprints[]).map((pi) => (
              <div key={pi.piId} className="border border-gray-200 dark:border-dark-500 rounded-lg overflow-hidden">
                <div className={`px-4 py-3 font-medium ${
                  pi.isSpecial ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 dark:bg-dark-600 text-gray-800 dark:text-gray-200'
                }`}>
                  {pi.name}
                  {pi.isSpecial && <span className="ml-2 text-xs bg-amber-200 px-2 py-0.5 rounded">Special</span>}
                  <span className="text-sm font-normal ml-4 text-gray-500 dark:text-gray-400">{(pi.sprints || []).length} sprints</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-dark-500 bg-gray-50 dark:bg-dark-600">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Sprint</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Start Date</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">End Date</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Duration</th>
                        {canAdmin && <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(pi.sprints || []).map((sprint) => {
                        const startDate = new Date(sprint.startDate);
                        const endDate = new Date(sprint.endDate);
                        const duration = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                        return (
                          <tr key={sprint.sprintId} className="border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600">
                            <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{sprint.name}</td>
                            <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                            <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                            <td className="py-3 px-4 text-center text-gray-500 dark:text-gray-400">{duration} days</td>
                            {canAdmin && (
                              <td className="py-3 px-4 text-right">
                                <button onClick={() => setEditingSprint(sprint)} className="text-primary-500 hover:text-primary-600 text-sm font-medium">Edit Dates</button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {(scheduleData?.items || []).length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No schedule data found</p>
            )}
          </div>
        ) : (
          /* Non-PI-based: show flat sprint list */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-500">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Sprint</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Start Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">End Date</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Duration</th>
                  {canAdmin && <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {((scheduleData?.items || []) as Sprint[]).map((sprint) => {
                  const startDate = new Date(sprint.startDate);
                  const endDate = new Date(sprint.endDate);
                  const duration = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <tr key={sprint.sprintId} className="border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600">
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{sprint.name}</td>
                      <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td className="py-3 px-4 text-center text-gray-500 dark:text-gray-400">{duration} days</td>
                      {canAdmin && (
                        <td className="py-3 px-4 text-right">
                          <button onClick={() => setEditingSprint(sprint)} className="text-primary-500 hover:text-primary-600 text-sm font-medium">Edit Dates</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(scheduleData?.items || []).length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No sprints found</p>
            )}
          </div>
        )}
      </div>

      {/* Edit project modal */}
      {showEditProject && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Project</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateProjectMutation.mutate(editProject);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="label">Project Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editProject.shortName}
                    onChange={(e) => setEditProject({ ...editProject, shortName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={editProject.description}
                    onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowEditProject(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={updateProjectMutation.isPending}>
                  {updateProjectMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create team modal */}
      {showCreateTeam && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Team</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createTeamMutation.mutate(newTeam);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="label">Team Name</label>
                  <input
                    type="text"
                    className="input"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowCreateTeam(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={createTeamMutation.isPending}>
                  {createTeamMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Initialize schedule modal */}
      {showInitSchedule && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Initialize {selectedYear} Schedule</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {(project.projectType || 'pi-based') === 'pi-based'
                ? 'This will create 7 Product Increments (PI 1-6 + PI X) with 26 sprints.'
                : (project.projectType) === 'regular-sprints'
                ? 'This will create 26 two-week sprints.'
                : (project.projectType) === 'weekly'
                ? 'This will create 52 one-week periods.'
                : 'This will create 12 monthly periods.'
              }
            </p>
            <form onSubmit={(e) => { e.preventDefault(); initScheduleMutation.mutate({ startDate: initStartDate || undefined }); }}>
              {((project.projectType || 'pi-based') === 'pi-based' || project.projectType === 'regular-sprints') && (
                <div className="mb-4">
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    className="input"
                    value={initStartDate}
                    onChange={(e) => setInitStartDate(e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {(project.projectType || 'pi-based') === 'pi-based'
                      ? 'PI 1 start date (usually 3rd Monday of January)'
                      : 'First sprint start date'
                    }
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowInitSchedule(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary" disabled={initScheduleMutation.isPending}>
                  {initScheduleMutation.isPending ? 'Initializing...' : 'Initialize'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit sprint dates modal */}
      {editingSprint && (
        <EditSprintDatesModal
          sprint={editingSprint}
          onClose={() => setEditingSprint(null)}
          onSave={(data) => updateSprintDatesMutation.mutate({ sprintId: editingSprint.sprintId, ...data })}
          isSaving={updateSprintDatesMutation.isPending}
        />
      )}

      {/* Add member modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Project Member</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedUserId) {
                  addMemberMutation.mutate({ userId: selectedUserId, accessLevel: selectedAccessLevel });
                }
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="label">User</label>
                  <select
                    className="input"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    required
                  >
                    <option value="">Select a user...</option>
                    {availableUsers.map((user) => (
                      <option key={user.userId} value={user.userId}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Access Level</label>
                  <select
                    className="input"
                    value={selectedAccessLevel}
                    onChange={(e) => setSelectedAccessLevel(e.target.value as 'viewer' | 'editor' | 'admin')}
                  >
                    <option value="viewer">Viewer - Can only view</option>
                    <option value="editor">Editor - Can edit data</option>
                    <option value="admin">Admin - Full access to project and teams</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => { setShowAddMember(false); setSelectedUserId(''); }} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={addMemberMutation.isPending || !selectedUserId}>
                  {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingMember && (
        <EditProjectMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSave={(data) =>
            updateMemberMutation.mutate({ userId: editingMember.userId, data })
          }
          isSaving={updateMemberMutation.isPending}
        />
      )}
    </div>
  );
}

function EditProjectMemberModal({
  member,
  onClose,
  onSave,
  isSaving,
}: {
  member: ProjectMember;
  onClose: () => void;
  onSave: (data: { role?: string; accessLevel?: string }) => void;
  isSaving: boolean;
}) {
  const [role, setRole] = useState(member.role);
  const [accessLevel, setAccessLevel] = useState(member.accessLevel);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ role, accessLevel });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold mb-4">Edit Member: {member.name}</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {PROJECT_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Access Level</label>
              <select
                className="input"
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value as 'viewer' | 'editor' | 'admin')}
              >
                {ACCESS_LEVELS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditSprintDatesModal({
  sprint,
  onClose,
  onSave,
  isSaving,
}: {
  sprint: Sprint;
  onClose: () => void;
  onSave: (data: { startDate: string; endDate: string; cascade: boolean }) => void;
  isSaving: boolean;
}) {
  const [startDate, setStartDate] = useState(sprint.startDate);
  const [endDate, setEndDate] = useState(sprint.endDate);
  const [cascade, setCascade] = useState(true);

  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const duration = Math.round((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Sprint Dates: {sprint.name}</h2>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ startDate, endDate, cascade }); }}>
          <div className="space-y-4">
            <div>
              <label className="label">Start Date</label>
              <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div>
              <label className="label">End Date</label>
              <input type="date" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
            <div className="bg-gray-100 dark:bg-dark-600 rounded-lg p-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Duration: <span className="font-medium">{duration} days</span></p>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cascade" checked={cascade} onChange={(e) => setCascade(e.target.checked)} className="h-4 w-4 text-primary-500 rounded border-gray-300 dark:border-dark-400 focus:ring-primary-500" />
              <label htmlFor="cascade" className="text-sm text-gray-600 dark:text-gray-300">Adjust all following sprints automatically</label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" className="btn-primary" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
